import { avg, record } from '../format';
import type { RatedPlayer, Team } from '../types';
import { SourceBadge } from './SourceBadge';

interface Props {
  title: string;
  accent: string;
  teams: Team[];
  teamId: number | null;
  onTeamChange: (teamId: number) => void;
  players: RatedPlayer[];
  selected: number[];
  onToggle: (playerId: number) => void;
  onClear: () => void;
}

/**
 * One side of the comparison: pick a team, then tick players. Selection order is
 * kept and shown as a badge — slot 1 of the left column is paired with slot 1 of
 * the right column.
 */
export function PlayerColumn({
  title,
  accent,
  teams,
  teamId,
  onTeamChange,
  players,
  selected,
  onToggle,
  onClear,
}: Props) {
  return (
    <div>
      <header style={{ display: 'flex', gap: 10, alignItems: 'baseline', marginBottom: 10 }}>
        <h3 style={{ color: accent }}>{title}</h3>
        <span className="tag">{selected.length} selected</span>
        {selected.length > 0 && (
          <button className="btn" style={{ marginLeft: 'auto', padding: '3px 10px' }} onClick={onClear}>
            Clear
          </button>
        )}
      </header>

      <select
        value={teamId ?? ''}
        onChange={(e) => onTeamChange(Number(e.target.value))}
        style={{ width: '100%', marginBottom: 10 }}
        aria-label={`${title} team`}
      >
        <option value="">Select a team…</option>
        {teams.map((t) => (
          <option key={t.id} value={t.id}>
            {t.rankingPos ? `${t.rankingPos}. ` : ''}
            {t.name}
          </option>
        ))}
      </select>

      {teamId === null ? (
        <div className="empty-state">Pick a team to list its players.</div>
      ) : players.length === 0 ? (
        <div className="empty-state">No players stored for this team.</div>
      ) : (
        <div className="player-list">
          {players.map((player) => {
            const order = selected.indexOf(player.playerId);
            return (
              <label
                key={player.playerId}
                className="player-row"
                data-selected={order >= 0}
                title={`Weighted score ${player.rating.score.toFixed(3)}`}
              >
                <input
                  type="checkbox"
                  checked={order >= 0}
                  onChange={() => onToggle(player.playerId)}
                  style={{ display: 'none' }}
                />
                <span className={order >= 0 ? 'order-badge' : 'order-badge empty'}>
                  {order >= 0 ? order + 1 : '0'}
                </span>
                <span className="name">
                  {player.displayName}{' '}
                  {player.statsSource !== 'season' ? (
                    <SourceBadge
                      source={player.statsSource}
                      carriedFrom={player.carriedFrom}
                      note={player.overrideNote}
                    />
                  ) : (
                    player.rating.lowSample && (
                      <span className="tag" title="Few single matches played">
                        thin
                      </span>
                    )
                  )}
                </span>
                <span className="stats">
                  {player.statsSource === 'none'
                    ? 'no games yet'
                    : `${record(player.singleWon, player.singleLost)} · ${avg(player.average)} avg`}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
